<template>
    <LocationForm :id="id" ref="form" @save="save" />
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { httpHelper } from '@/utils'
import LocationForm from '../LocationForm'

export default {
    name: 'EditLocationView',
    components: {
        LocationForm,
    },
    props: {
        id: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            initialValues: {},
            saved: false,
        }
    },
    computed: {
        ...mapGetters('auth', ['isAdminOrStaff']),
        changed() {
            return this.locationChanged || this.permissionsChanged
        },
        locationChanged() {
            const {
                name,
                description,
                geofence_tolerance,
                geofence,
                trailer_detection_disabled,
            } = this.$refs.form.getValues()

            return (
                this.initialValues.name !== name ||
                this.initialValues.description !== description ||
                this.initialValues.geofence_tolerance !== geofence_tolerance ||
                !geofence ||
                this.initialValues.geofence.coordinates[0].length !==
                    geofence.coordinates[0].length ||
                // use tolerance of 1e-6 in the comparison of coordinates to avoid
                // false detection of changes
                this.initialValues.geofence.coordinates[0]
                    .map((k, i) => [
                        [k[0], geofence.coordinates[0][i][0]],
                        [k[1], geofence.coordinates[0][i][1]],
                    ])
                    .flat()
                    .some(p => Math.abs(p[0] - p[1]) > 1e-6) ||
                this.initialValues.trailer_detection_disabled !==
                    trailer_detection_disabled
            )
        },
        permissionsChanged() {
            const {
                rolesViewingAccess,
                rolesEditingAccess,
            } = this.$refs.form.getPermissions()

            return (
                this.initialValues.rolesViewingAccess !== rolesViewingAccess ||
                this.initialValues.rolesEditingAccess !== rolesEditingAccess
            )
        },
    },
    async mounted() {
        this.$refs.form.setLoading(true)
        const { data } = await httpHelper.get(`/locations/${this.id}/`)
        if (this.isAdminOrStaff) {
            const allPermissions = await httpHelper.get(
                `/role-location-permissions/`
            )
            const permissionData = allPermissions.data.results.filter(
                item => item.location == this.id
            )
            this.initialValues.rolesViewingAccess = permissionData
                .filter(item => item.access === 'READ')
                .map(item => item.role)
            this.initialValues.rolesEditingAccess = permissionData
                .filter(item => item.access === 'WRITE')
                .map(item => item.role)
        }
        this.initialValues.name = data.name
        this.initialValues.owner = data.owner
        this.initialValues.description = data.description
        this.initialValues.geofence = data.geofence
        this.initialValues.geofence_tolerance = data.geofence_tolerance
        this.initialValues.trailer_detection_disabled =
            data.trailer_detection_disabled
        this.$refs.form.setValues(this.initialValues)
        this.setActiveLocationOnMap(data)
        this.$refs.form.setLoading(false)
    },
    destroyed() {
        this.setActiveLocationOnMap(null)
    },
    methods: {
        ...mapMutations('location', [
            'setActiveLocationOnMap',
            'updateLocation',
        ]),
        async save(values) {
            if (!this.changed) {
                this.saved = true
                this.$refs.form.saved(this.id)
                return
            }
            const { data } = await httpHelper.put(`/locations/${this.id}/`, {
                ...values,
                owner: this.initialValues.owner,
            })
            this.updateLocation(data)
            this.saved = true
            this.$refs.form.saved(data.id)
            if (this.isAdminOrStaff) {
                this.manageAccess()
            }
        },
        async manageAccess() {
            const {
                rolesViewingAccess,
                rolesEditingAccess,
            } = this.$refs.form.getPermissions()

            const { data } = await httpHelper.get('/role-location-permissions/')
            const permissionData = data.results.filter(
                item => item.location == this.id
            )

            const rolesViewingAccessExisting = permissionData
                .filter(item => item.access === 'READ')
                .map(item => item.role)

            const rolesEditingAccessExisting = permissionData
                .filter(item => item.access === 'WRITE')
                .map(item => item.role)

            permissionData.forEach(item => {
                if (
                    item.access === 'READ' &&
                    rolesEditingAccess.includes(item.role)
                ) {
                    rolesEditingAccessExisting.push(item.role)
                    return httpHelper.patch(
                        `/role-location-permissions/${item.id}/`,
                        { access: 'WRITE' }
                    )
                }

                if (
                    item.access === 'WRITE' &&
                    rolesViewingAccess.includes(item.role)
                ) {
                    rolesViewingAccessExisting.push(item.role)
                    return httpHelper.patch(
                        `/role-location-permissions/${item.id}/`,
                        { access: 'READ' }
                    )
                }

                if (
                    (item.access === 'READ' &&
                        !rolesViewingAccess.includes(item.role)) ||
                    (item.access === 'WRITE' &&
                        !rolesEditingAccess.includes(item.role))
                ) {
                    return httpHelper.drop(
                        `/role-location-permissions/${item.id}/`
                    )
                }
            })

            rolesViewingAccess.forEach(role => {
                if (!rolesViewingAccessExisting.includes(role)) {
                    httpHelper.post('/role-location-permissions/', {
                        location: this.id,
                        role,
                        access: 'READ',
                    })
                }
            })

            rolesEditingAccess.forEach(role => {
                if (!rolesEditingAccessExisting.includes(role)) {
                    httpHelper.post('/role-location-permissions/', {
                        location: this.id,
                        role,
                        access: 'WRITE',
                    })
                }
            })
        },
    },
    beforeRouteLeave(to, from, next) {
        if (!this.changed || this.saved) {
            next()
            return
        }

        this.$refs.form.confirmLeave(this.$t('confirmLeave'), confirmed => {
            next(confirmed)
        })
    },
}
</script>

<i18n>
{
    "en": {
        "confirmLeave": "Really discard changes?"
    },
    "de": {
        "confirmLeave": "Änderungen wirklich verwerfen?"
    },
    "fr": {
        "confirmLeave": "Souhaitez-vous réellement abandonner les modifications ?"
    }
}
</i18n>
